import * as React from 'react'
import { Progress } from 'reactstrap'
import { inject, observer } from 'mobx-react'
import { Modal, Button, Icon, Upload, message, Alert } from 'antd'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import {
  parseErrorMsg,
  uploadFileToS3Bucket,
  sleep,
  DropzoneFile,
  UploadFile,
} from '../../../../../utilities/helpers'
import { TransfersApi, TransfersModels } from '@getgreenline/transfers'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  onClose: () => void
  onSuccess: (receipt: TransfersModels.ITransferProductContract[]) => void
  transferId?: string
}

interface State {
  isUploading: boolean
  uploadStepsTaken: number
  progressMessage?: string
  errorMessage?: string
  errorMessageDetails?: string[]
  receiptFileName?: string
  importedProducts?: TransfersModels.ITransferProductContract[]
}

@inject('currentCompanyStore')
@observer
export class ImportTransferModal extends React.Component<Props, State> {
  private uploadTotalSteps = 4

  constructor(props: Props) {
    super(props)
    this.state = {
      isUploading: false,
      uploadStepsTaken: 0,
    }
  }

  uploadToS3 = (
    file: UploadFile,
    fileName: string,
    signedUrl: string,
    contentType: string,
  ): void => {
    const xhr = uploadFileToS3Bucket(file, signedUrl, contentType)

    xhr.onload = async () => {
      if (xhr.status !== 200) {
        return this.setState({
          errorMessage: 'An error occurred while uploading your file',
        })
      }

      await this.setState({
        progressMessage: 'Analyzing the file...',
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
      })

      this.xhrOnLoad(fileName)
    }
  }

  async xhrOnLoad(s3FileName: string) {
    const company = this.props.currentCompanyStore!.company!
    const { transferId } = this.props
    if (!transferId) {
      return this.setState({
        errorMessage: 'Please save the transfer before importing',
      })
    }

    // Step 4, Parse the file
    try {
      const importedProducts = await TransfersApi.importProducts(company.id, transferId, s3FileName)
      this.setState({
        isUploading: false,
        uploadStepsTaken: this.state.uploadStepsTaken + 1,
        importedProducts,
      })
    } catch (error) {
      const errorMsg = parseErrorMsg(error)
      const errorMsgArr = errorMsg?.split('\n')

      if (errorMsgArr) {
        let errorMessageDetails
        if (errorMsgArr.length > 1) {
          errorMessageDetails = errorMsgArr.splice(1)
        }

        this.setState({
          errorMessage: errorMsgArr[0],
          errorMessageDetails,
        })
      }
    }
  }

  async onDrop(files: DropzoneFile[]) {
    const company = this.props.currentCompanyStore!.company!

    // Step 1, Check the file
    await this.setState({
      isUploading: true,
      progressMessage: 'Checking the file',
      uploadStepsTaken: this.state.uploadStepsTaken + 1,
    })

    // Step 2, Validate the file
    if (files.length === 0) {
      return this.setState({
        errorMessage: 'We could not find any file',
        isUploading: false,
      })
    }
    const file = files[0]
    const fullFileName = file.name
    const fileExtension = fullFileName.split('.').pop()?.toLowerCase()
    if (fileExtension !== 'csv') {
      return this.setState({
        errorMessage: 'You must upload a csv file',
      })
    }
    const contentType = file.type
    const fileObject = { file, fullFileName, fileExtension, contentType }

    // 500ms delay to show the first progress message
    await sleep(500)

    // Step 3, Upload the file to S3
    await this.setState({
      progressMessage: 'Uploading the file...',
      uploadStepsTaken: this.state.uploadStepsTaken + 1,
    })
    const { fileName, signedUrl } =
      await this.props.currentCompanyStore!.getSignedTempFileUploadUrl(fileExtension, contentType)
    this.setState({ receiptFileName: fileName })
    this.uploadToS3(file.originFileObj, fileName, signedUrl, contentType)
  }

  get footer() {
    return (
      <>
        <Button key='cancel' onClick={() => this.props.onClose()}>
          Cancel
        </Button>
        {this.state.importedProducts && (
          <Button
            key='submit'
            type='primary'
            onClick={() => this.props.onSuccess(this.state.importedProducts!)}
          >
            Import
          </Button>
        )}
      </>
    )
  }

  get errorMessage() {
    const { errorMessage, errorMessageDetails } = this.state

    if (errorMessage) {
      return (
        <div className='alert alert-danger' role='alert'>
          <div className='mb-1'>{errorMessage}</div>
          {errorMessageDetails && (
            <ul className='mb-0'>
              {errorMessageDetails.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}
        </div>
      )
    }

    return null
  }

  render() {
    const { isUploading, progressMessage, importedProducts } = this.state

    return (
      <Modal
        title='Import'
        visible={true}
        maskClosable={false}
        onCancel={this.props.onClose}
        footer={this.footer}
      >
        {this.errorMessage}

        {isUploading ? (
          <>
            {!this.state.errorMessage && (
              <Progress
                animated
                value={Math.floor((this.state.uploadStepsTaken / this.uploadTotalSteps) * 100)}
              >
                {progressMessage}
              </Progress>
            )}
          </>
        ) : importedProducts ? (
          <>
            <div>
              <b className='text-success'>{importedProducts.length}</b> SKUs found
            </div>
            <ul>
              {importedProducts.map((product) => {
                return <li key={product.id}>{product.sku}</li>
              })}
            </ul>
          </>
        ) : (
          <>
            <Alert
              type='info'
              showIcon
              message='Required CSV fields'
              description={
                <>
                  <ul>
                    <li>
                      SKU <span className='text-danger'>*required</span>
                    </li>
                    <li>
                      Quantity <span className='text-danger'>*required</span>
                    </li>
                  </ul>
                </>
              }
            />
            <br />
            <Upload.Dragger
              name='file'
              multiple={false}
              onChange={(info: any) => this.onDrop(info.fileList)}
            >
              <p className='ant-upload-drag-icon'>
                <Icon type='inbox' />
              </p>
              <p className='ant-upload-text'>Click or drag file to upload</p>
            </Upload.Dragger>
          </>
        )}
      </Modal>
    )
  }
}
